// composables/useTadbeesaChannel.js
//
// Resolves the «تدبيسة النادي» channel's id + PoolShare rate — the one lookup
// (2026-09-22 brief: `GET channels?filter[username]=tadbeesa`) every تدبيسة
// surface needs to know which channel it's talking about: the return-button
// gate (utils/supplierReturn.js), the per-size cap, the shelf strip and the
// /tadbeesa/ page. None of them should each fire their own request, so this
// resolves ONCE PER SIGNED-IN USER — a module-level cache plus a single
// shared in-flight promise, so N concurrent callers cost exactly one HTTP
// request — and re-resolves whenever `authStore.user.id` changes (logout,
// act-as enter/exit, a plain account switch).
//
// A failed lookup never throws into the UI: every caller degrades to "no
// تدبيسة channel known this session" (hides the strip, shows an ordinary
// return button, skips the per-size cap) — the safe direction for a lookup
// nothing here treats as load-bearing. Callers are never retried
// automatically; `failed` tells a failure apart from "there is no such
// channel", and `retry()` is the one explicit way to ask again — the shelf
// page, where the lookup IS load-bearing, offers it as a retry button.

import { computed, ref, watch } from 'vue';
import { jsonApiClient } from '@/httpConfig/config/axios.js';
import { useAuthStore } from '@/stores/authStore.js';

const TADBEESA_USERNAME = 'tadbeesa';

const EMPTY_RESULT = Object.freeze({ channelId: null, poolShareRate: null, failed: false });
const FAILED_RESULT = Object.freeze({ channelId: null, poolShareRate: null, failed: true });

// Module-level cache, shared by every useTadbeesaChannel() caller.
// `resolvedForUserId` is `undefined` until the first resolution ever lands —
// deliberately distinct from `null`, which is a real, resolved answer for
// "signed out".
let resolvedForUserId;
let cached = EMPTY_RESULT;
let inFlightForUserId;
let inFlightPromise = null;

async function fetchChannel() {
  try {
    const response = await jsonApiClient.get('channels', {
      params: { 'filter[username]': TADBEESA_USERNAME },
    });
    const channel = response?.data?.data?.[0] ?? null;
    if (channel?.id == null) return EMPTY_RESULT;

    return {
      channelId: String(channel.id),
      poolShareRate: channel.poolShareRate ?? null,
      failed: false,
    };
  } catch {
    return FAILED_RESULT;
  }
}

/**
 * Resolve (or reuse) the module-level answer for one user id. Concurrent
 * calls for the SAME uid before the first settles all share one in-flight
 * promise; a call for a NEW uid — mid-flight or not — starts its own fetch.
 */
async function resolveForUser(uid) {
  if (resolvedForUserId === uid) return cached;

  if (inFlightForUserId !== uid || !inFlightPromise) {
    inFlightForUserId = uid;
    inFlightPromise = fetchChannel();
  }

  const result = await inFlightPromise;

  // Only commit if nobody resolved a NEWER uid while this one was in flight
  // (a fast logout / act-as swap mid-request) — a stale answer must never
  // overwrite the identity she has already moved on to.
  if (inFlightForUserId === uid) {
    resolvedForUserId = uid;
    cached = result;
  }

  return resolvedForUserId === uid ? cached : result;
}

/** Test-only: clear the module-level cache between specs (mirrors the
 *  `__resetPoolaRealtimeRegistry` pattern in usePoolaRealtime.js). */
export function __resetTadbeesaChannelCache() {
  resolvedForUserId = undefined;
  cached = EMPTY_RESULT;
  inFlightForUserId = undefined;
  inFlightPromise = null;
}

export function useTadbeesaChannel() {
  const authStore = useAuthStore();

  const currentUserId = () => authStore.user?.id ?? null;
  const initiallyResolved = resolvedForUserId === currentUserId();

  const channelId = ref(initiallyResolved ? cached.channelId : null);
  const poolShareRate = ref(initiallyResolved ? cached.poolShareRate : null);
  const failed = ref(initiallyResolved ? cached.failed : false);
  const ready = ref(initiallyResolved);

  async function resolve() {
    const uid = currentUserId();

    // No signed-in user — nothing to look up, and nothing worth caching:
    // every consumer already gates on being signed in / a member, so this is
    // a cheap, immediate no-op rather than a request.
    if (uid == null) {
      channelId.value = null;
      poolShareRate.value = null;
      failed.value = false;
      ready.value = true;
      return;
    }

    const result = await resolveForUser(uid);

    // She may have moved on (logged out, act-as swap) while this awaited —
    // don't paint a since-superseded identity's answer.
    if (currentUserId() !== uid) return;

    channelId.value = result.channelId;
    poolShareRate.value = result.poolShareRate;
    failed.value = result.failed === true;
    ready.value = true;
  }

  /**
   * Ask again after a FAILED lookup — the one path that re-fetches for the
   * same user. A settled answer (the channel, or "there is none") is kept:
   * only a failure is dropped from the cache. `ready` goes false while it
   * runs, so a caller shows its loader, not the stale failure.
   */
  async function retry() {
    const uid = currentUserId();
    if (uid != null && resolvedForUserId === uid && cached.failed) {
      resolvedForUserId = undefined;
      cached = EMPTY_RESULT;
    }
    if (uid != null && inFlightForUserId === uid) {
      // A settled in-flight promise would otherwise hand back the failure.
      inFlightPromise = null;
    }
    ready.value = false;
    await resolve();
  }

  watch(currentUserId, () => {
    ready.value = false;
    resolve();
  });

  resolve();

  return {
    channelId,
    poolShareRate,
    failed: computed(() => failed.value),
    ready: computed(() => ready.value),
    resolve,
    retry,
  };
}
